import { useMemo, useCallback } from "react";
import { useDetailNavigationOptional } from "@/contexts/DetailNavigationContext";
import { getEntityDetailConfig, isEntityRegistryKey } from "@/lib/entity-registry";
import { useOpenModal } from "@/lib/modals";
import { getModalConfig, isModalConfigured } from "@/lib/modals/url/modal-url-config";
import type { DetailPageConfig } from "../../UniversalDetailPage/types/detailPage";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types/shared";

export function useDetailModalHeader<T extends BaseEntity>({
  item,
  entityName,
  config,
}: {
  item: T | undefined;
  entityName: string;
  config: SimpleTableConfig<T>;
}) {
  const openModal = useOpenModal();

  // Get detail config for entities that have one (for titleTemplate/descriptionTemplate)
  const detailConfig = useMemo(() => {
    // First check if config has detailConfig property
    const tableConfig = config as any;
    if (tableConfig.detailConfig) {
      return tableConfig.detailConfig as DetailPageConfig<any>;
    }

    // Fallback to entity registry for detail config
    if (isEntityRegistryKey(entityName)) {
      return getEntityDetailConfig(entityName);
    }

    return null;
  }, [config, entityName]);

  // Compute header title — must be before any conditional returns (Rules of Hooks)
  const headerTitle = useMemo(() => {
    if (!item) return config.displayName;

    // First check detail config for titleTemplate
    if (detailConfig?.titleTemplate) {
      if (typeof detailConfig.titleTemplate === "function") {
        return detailConfig.titleTemplate(item);
      }
      // If it's a string template, replace {title} placeholder
      return detailConfig.titleTemplate.replace(
        "{title}",
        String(item.title || item.name || item.id || "")
      );
    }

    // Fallback: check if table config has titleTemplate (for backward compatibility)
    const tableConfig = config as any;
    if (tableConfig.titleTemplate) {
      if (typeof tableConfig.titleTemplate === "function") {
        return tableConfig.titleTemplate(item);
      }
      return tableConfig.titleTemplate.replace(
        "{title}",
        String(item.title || item.name || item.id || "")
      );
    }

    // Final fallback to default behavior
    return String(item.title || item.name || `${config.displayName} Details`);
  }, [item, config, detailConfig]);

  // Compute header subtitle/description — must be before any conditional returns (Rules of Hooks)
  const headerSubtitle = useMemo(() => {
    if (!item) return "";

    // First check detail config for descriptionTemplate
    if (detailConfig?.descriptionTemplate) {
      if (typeof detailConfig.descriptionTemplate === "function") {
        return detailConfig.descriptionTemplate(item);
      }
      // If it's a string template, return as-is
      return detailConfig.descriptionTemplate;
    }

    // Fallback: check if table config has descriptionTemplate (for backward compatibility)
    const tableConfig = config as any;
    if (tableConfig.descriptionTemplate) {
      if (typeof tableConfig.descriptionTemplate === "function") {
        return tableConfig.descriptionTemplate(item);
      }
      return tableConfig.descriptionTemplate;
    }

    // Final fallback to empty string
    return "";
  }, [item, config, detailConfig]);

  // Get navigation context (optional)
  const navigationContext = useDetailNavigationOptional();

  // Compute if we can navigate
  const canNavigate =
    navigationContext &&
    navigationContext.ids.length > 1 &&
    navigationContext.entityName === entityName &&
    navigationContext.index !== undefined;

  const canGoPrevious = canNavigate && navigationContext.index > 0;
  const canGoNext = canNavigate && navigationContext.index < navigationContext.ids.length - 1;

  // Handle previous/next navigation
  const handlePrevious = useCallback(() => {
    if (!canGoPrevious || !navigationContext) return;

    const prevIndex = navigationContext.index - 1;
    const prevId = navigationContext.ids[prevIndex];

    // Update context
    navigationContext.setContext(navigationContext.ids, prevIndex, entityName);

    const modalId = `${entityName}-detail`;
    if (!isModalConfigured(modalId)) {
      return;
    }

    const modalConfig = getModalConfig(modalId);
    openModal(modalId, { [modalConfig.pluralParam]: prevId });
  }, [canGoPrevious, navigationContext, entityName, openModal]);

  const handleNext = useCallback(() => {
    if (!canGoNext || !navigationContext) return;

    const nextIndex = navigationContext.index + 1;
    const nextId = navigationContext.ids[nextIndex];

    // Update context
    navigationContext.setContext(navigationContext.ids, nextIndex, entityName);

    const modalId = `${entityName}-detail`;
    if (!isModalConfigured(modalId)) {
      return;
    }

    const modalConfig = getModalConfig(modalId);
    openModal(modalId, { [modalConfig.pluralParam]: nextId });
  }, [canGoNext, navigationContext, entityName, openModal]);

  return {
    headerTitle,
    headerSubtitle,
    canNavigate,
    canGoPrevious,
    canGoNext,
    handlePrevious,
    handleNext,
  };
}
